import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    LinearProgress,
    TextField,
    Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { motion, AnimatePresence } from 'framer-motion';
import { CustomDatePicker } from '../../../components/DatePicker/CustomDatePicker';
import { ContestRichSelect } from './ContestRichSelect';
import { getPhaseSelectOptionsFromValues } from './contestSelectOptions';
import type { ContestAiAssistContext, ContestWizardState } from './types';
import { datePickerValueToYyyyMmDd, isDateOnOrAfterToday, todayStart } from './dateUtils';
import { parseCsvSelection, toggleCsvSelection } from './csvSelection';
import { getAwardBudgetSelectOptions, getAwardLayoutSelectOptions } from './awardPresets';
import { generateAwardsFromAnswers } from './generateAwardsFromAnswers';
import { generateJudgingFromAnswers } from './generateJudgingFromAnswers';
import { generateTimelinePhasesFromAnswers } from './generateTimelineFromAnswers';
import type { ICampaignCategory } from '../../../components/EsForm/CampaignCategorySelector';
import {
    defaultCriteriaSelection,
    getContestTypeSelectOptions,
    getCriteriaOptionsForContestType,
} from './judgingCriteriaPresets';
import {
    defaultAiSelectedPhases,
    formatPhaseLabel,
    parseSelectedPhases,
    phaseOptionsForCampaign,
    serializeSelectedPhases,
    toggleSelectedPhase,
} from './phaseConfig';
import { contestWizardDatePickerSx, editorialHeadlineSx, editorialInputSx, midnight } from './midnightStyles';

export interface ContestAiAssistResult {
    title?: string;
    description?: string;
    categoryName?: string;
    categoryDescription?: string;
    tiers?: Array<{ label: string; fee: number; slots?: number }>;
    requirements?: string[];
    phases?: Array<{ phase: string; date_started: string; expiration_date: string }>;
    contest_phases_enabled?: boolean;
    score_criteria?: string[];
    prize_structure?: NonNullable<ICampaignCategory['prize_structure']>;
}

interface QuestionOption {
    value: string;
    label: string;
}

interface Question {
    id: string;
    prompt: string;
    placeholder: string;
    multiline?: boolean;
    type?: 'text' | 'select' | 'date' | 'multiselect' | 'richselect';
    options?: QuestionOption[];
}

const QUESTIONS: Record<ContestAiAssistContext, Question[]> = {
    identity: [
        {
            id: 'goal',
            prompt: 'What is the main goal of this campaign?',
            placeholder: 'e.g. Discover emerging filmmakers for our summer anthology…',
            multiline: true,
        },
        {
            id: 'tone',
            prompt: 'What tone or mood should submissions reflect?',
            placeholder: 'e.g. Cinematic, bold, midnight urban aesthetic…',
        },
        {
            id: 'audience',
            prompt: 'Who is your target audience?',
            placeholder: 'e.g. Gen Z film enthusiasts, festival programmers…',
        },
    ],
    category: [
        {
            id: 'category_focus',
            prompt: 'What should this category focus on?',
            placeholder: 'e.g. 60-second lifestyle reels shot at night…',
            multiline: true,
        },
        {
            id: 'prize_budget',
            prompt: 'Approximate prize budget for this category (USD)?',
            placeholder: 'e.g. 5000',
        },
        {
            id: 'tier_style',
            prompt: 'Preferred tier style?',
            placeholder: 'e.g. Bronze / Silver / Gold or Standard / Premium',
        },
    ],
    requirements: [
        {
            id: 'must_include',
            prompt: 'What must every submission include?',
            placeholder: 'e.g. Original score, on-screen title card…',
            multiline: true,
        },
        {
            id: 'restrictions',
            prompt: 'Any restrictions?',
            placeholder: 'e.g. No stock footage, max 3 minutes…',
            multiline: true,
        },
    ],
    timeline: [],
    judging: [],
    awards: [],
};

function buildJudgingQuestions(answers: Record<string, string>): Question[] {
    const contestType = answers.contest_type || '';
    return [
        {
            id: 'contest_type',
            prompt: 'What type of contest is this?',
            placeholder: '',
            type: 'richselect',
        },
        {
            id: 'selected_criteria',
            prompt: 'Choose judging criteria for your scorecard (select all that apply)',
            placeholder: '',
            type: 'multiselect',
            options: getCriteriaOptionsForContestType(contestType || 'acting'),
        },
    ];
}

function buildAwardQuestions(): Question[] {
    return [
        {
            id: 'award_layout',
            prompt: 'How should prizes be structured?',
            placeholder: '',
            type: 'richselect',
        },
        {
            id: 'award_budget',
            prompt: 'What prize budget level fits this category?',
            placeholder: '',
            type: 'richselect',
        },
    ];
}

function buildTimelineQuestions(
    submissionState: ContestWizardState['submission_state'],
    answers: Record<string, string>,
): Question[] {
    const allPhaseOptions = phaseOptionsForCampaign(submissionState).map(p => ({
        value: p,
        label: formatPhaseLabel(p),
    }));
    const selected = parseSelectedPhases(answers.selected_phases);
    const firstPhaseOptions =
        selected.length > 0 ? selected.map(p => ({ value: p, label: formatPhaseLabel(p) })) : allPhaseOptions;

    return [
        {
            id: 'selected_phases',
            prompt: 'Which phases does this campaign include?',
            placeholder: '',
            type: 'multiselect',
            options: allPhaseOptions,
        },
        {
            id: 'first_phase',
            prompt: 'Which of those phases should start first?',
            placeholder: '',
            type: 'select',
            options: firstPhaseOptions,
        },
        {
            id: 'start_date',
            prompt: 'When does the first phase start?',
            placeholder: '',
            type: 'date',
        },
        {
            id: 'phase_duration_days',
            prompt: 'How many days should each phase run?',
            placeholder: 'e.g. 14',
        },
    ];
}

function generateFromAnswers(
    context: ContestAiAssistContext,
    answers: Record<string, string>,
    submissionState: ContestWizardState['submission_state'],
    awardCategory?: ICampaignCategory,
): ContestAiAssistResult {
    if (context === 'timeline') {
        return generateTimelinePhasesFromAnswers(answers, submissionState);
    }
    if (context === 'judging') {
        return generateJudgingFromAnswers(answers);
    }
    if (context === 'awards' && awardCategory) {
        return generateAwardsFromAnswers(awardCategory, answers);
    }
    const goal = answers.goal || answers.category_focus || answers.must_include || 'creative campaign';
    const tone = answers.tone || 'cinematic and contemporary';
    const audience = answers.audience || 'creators and film enthusiasts';
    const budget = parseInt(String(answers.prize_budget || '3000').replace(/\D/g, ''), 10) || 3000;
    const tierStyle = (answers.tier_style || 'Bronze, Silver, Gold')
        .split(/[,/]/)
        .map(s => s.trim())
        .filter(Boolean);

    if (context === 'identity') {
        const titleWords = goal
            .split(/\s+/)
            .slice(0, 4)
            .map(w => w.charAt(0).toUpperCase() + w.slice(1));
        return {
            title: `${titleWords.join(' ')} Campaign`.slice(0, 80),
            description: `<p><strong>Vision:</strong> ${goal}</p><p><strong>Tone:</strong> ${tone}</p><p><strong>Audience:</strong> ${audience}</p><p>Submit your best work that aligns with our brand values and creative direction.</p>`,
            requirements: [
                `Reflect a ${tone} aesthetic`,
                'Original work only — no unauthorized third-party content',
                'Follow all technical specifications in the brief',
            ],
        };
    }

    if (context === 'category') {
        const tierLabels = tierStyle.length >= 2 ? tierStyle : ['Bronze', 'Silver', 'Gold'];
        const fees = tierLabels.map((_, i) => Math.round((budget / tierLabels.length) * (0.5 + i * 0.25)));
        return {
            categoryName: goal.split(/\s+/).slice(0, 5).join(' ') || 'Featured Category',
            categoryDescription: `<p>${goal}</p><p>Entries will be judged on creativity, technical execution, and alignment with the brief.</p>`,
            tiers: tierLabels.map((label, i) => ({
                label,
                fee: Math.max(49, fees[i] || 99),
                slots: i === tierLabels.length - 1 ? 5 : 20,
            })),
        };
    }

    const restrictions = answers.restrictions || 'Standard platform guidelines apply';
    return {
        requirements: [
            answers.must_include || 'Meet all brief requirements',
            restrictions,
            'Submit before the campaign deadline',
        ],
    };
}

function richSelectOptionsForQuestion(
    context: ContestAiAssistContext,
    questionId: string,
): ReturnType<typeof getContestTypeSelectOptions> {
    if (context === 'awards') {
        if (questionId === 'award_layout') return getAwardLayoutSelectOptions();
        if (questionId === 'award_budget') return getAwardBudgetSelectOptions();
    }
    return getContestTypeSelectOptions();
}

export interface ContestAiAssistDialogProps {
    open: boolean;
    context: ContestAiAssistContext;
    submissionState?: ContestWizardState['submission_state'];
    awardSourceCategory?: ICampaignCategory;
    onClose: () => void;
    onApply: (result: ContestAiAssistResult) => void;
}

function isQuestionAnswered(question: Question, answers: Record<string, string>): boolean {
    const value = answers[question.id]?.trim() ?? '';

    if (question.type === 'multiselect') {
        if (question.id === 'selected_phases') {
            return parseSelectedPhases(answers.selected_phases).length > 0;
        }
        return parseCsvSelection(answers[question.id]).length > 0;
    }

    if (question.type === 'richselect') {
        return Boolean(value);
    }

    if (!value) return false;

    if (question.type === 'date') return isDateOnOrAfterToday(value);

    if (question.id === 'first_phase') {
        const selected = parseSelectedPhases(answers.selected_phases);
        return selected.length === 0 || selected.includes(value);
    }

    if (question.id === 'phase_duration_days') {
        const days = parseInt(value.replace(/\D/g, ''), 10);
        return !Number.isNaN(days) && days > 0;
    }

    return true;
}

export function ContestAiAssistDialog({
    open,
    context,
    submissionState,
    awardSourceCategory,
    onClose,
    onApply,
}: ContestAiAssistDialogProps) {
    const submission = submissionState ?? 'public_submission';
    const [index, setIndex] = useState(0);
    const [answers, setAnswers] = useState<Record<string, string>>({});
    const [generating, setGenerating] = useState(false);

    const questions = useMemo(() => {
        if (context === 'timeline') {
            return buildTimelineQuestions(submission, answers);
        }
        if (context === 'judging') {
            return buildJudgingQuestions(answers);
        }
        if (context === 'awards') {
            return buildAwardQuestions();
        }
        return QUESTIONS[context];
    }, [context, submission, answers]);

    useEffect(() => {
        if (!open || context !== 'timeline') return;
        setAnswers(prev => {
            if (prev.selected_phases) return prev;
            return {
                ...prev,
                selected_phases: serializeSelectedPhases(defaultAiSelectedPhases(submission)),
            };
        });
    }, [open, context, submission]);

    useEffect(() => {
        if (!open || context !== 'judging') return;
        setAnswers(prev => {
            if (prev.contest_type) return prev;
            return { ...prev, contest_type: 'acting' };
        });
    }, [open, context]);

    useEffect(() => {
        if (!open || context !== 'awards') return;
        setAnswers(prev => {
            if (prev.award_layout) return prev;
            return { ...prev, award_layout: 'podium_tiers', award_budget: 'medium' };
        });
    }, [open, context]);

    const current = questions[index];
    const progress = ((index + 1) / questions.length) * 100;

    const handleNext = () => {
        if (current?.id === 'contest_type' && answers.contest_type) {
            setAnswers(prev => ({
                ...prev,
                selected_criteria: defaultCriteriaSelection(prev.contest_type || answers.contest_type),
            }));
        }

        if (current?.id === 'selected_phases') {
            const selected = parseSelectedPhases(answers.selected_phases);
            if (answers.first_phase && !selected.includes(answers.first_phase)) {
                setAnswers(prev => ({
                    ...prev,
                    first_phase: selected[0] ?? '',
                }));
            } else if (!answers.first_phase && selected[0]) {
                setAnswers(prev => ({ ...prev, first_phase: selected[0] }));
            }
        }

        if (index < questions.length - 1) {
            setIndex(i => i + 1);
            return;
        }
        setGenerating(true);
        window.setTimeout(() => {
            const result = generateFromAnswers(context, answers, submission, awardSourceCategory);
            setGenerating(false);
            onApply(result);
            setIndex(0);
            setAnswers({});
            onClose();
        }, 700);
    };

    const handleClose = () => {
        setIndex(0);
        setAnswers({});
        setGenerating(false);
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    bgcolor: midnight.surfaceContainer,
                    borderRadius: 3,
                    border: `1px solid ${midnight.outline}`,
                },
            }}>
            <DialogTitle sx={{ ...editorialHeadlineSx, color: midnight.primary, fontSize: 22, pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AutoAwesomeIcon sx={{ color: midnight.secondary }} />
                    AI Assistant
                </Box>
            </DialogTitle>
            <DialogContent>
                <Typography sx={{ fontSize: 13, color: midnight.onSurfaceVariant, mb: 3 }}>
                    {context === 'timeline'
                        ? 'Select every phase you need, which opens first, start date, and duration — we will schedule them in campaign order.'
                        : context === 'judging'
                        ? 'Pick your contest discipline, then choose from six tailored judging criteria for your scorecard.'
                        : context === 'awards'
                        ? 'We use this category’s submission tiers to suggest placements and prize amounts — edit anything after applying.'
                        : 'Answer a few questions — we will draft copy and structure for you to review and edit.'}
                </Typography>
                <LinearProgress
                    variant="determinate"
                    value={progress}
                    sx={{
                        mb: 3,
                        height: 4,
                        borderRadius: 2,
                        bgcolor: midnight.surfaceHighest,
                        '& .MuiLinearProgress-bar': { bgcolor: midnight.secondary },
                    }}
                />
                <AnimatePresence mode="wait">
                    {!generating && current && (
                        <motion.div
                            key={current.id}
                            initial={{ opacity: 0, x: 16 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -16 }}
                            transition={{ duration: 0.25 }}>
                            <Typography sx={{ fontWeight: 600, color: midnight.onSurface, mb: 2 }}>
                                {current.prompt}
                            </Typography>
                            {current.type === 'multiselect' ? (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    {(current.options || []).map(opt => {
                                        const fieldId = current.id;
                                        const selected =
                                            fieldId === 'selected_phases'
                                                ? parseSelectedPhases(answers.selected_phases)
                                                : parseCsvSelection(answers[fieldId]);
                                        const isOn = selected.includes(opt.value);
                                        return (
                                            <Chip
                                                key={opt.value}
                                                label={opt.label}
                                                onClick={() =>
                                                    setAnswers(prev => ({
                                                        ...prev,
                                                        [fieldId]:
                                                            fieldId === 'selected_phases'
                                                                ? toggleSelectedPhase(prev.selected_phases, opt.value)
                                                                : toggleCsvSelection(prev[fieldId], opt.value),
                                                    }))
                                                }
                                                sx={{
                                                    fontWeight: 600,
                                                    borderRadius: 2,
                                                    height: 'auto',
                                                    py: 0.75,
                                                    '& .MuiChip-label': {
                                                        whiteSpace: 'normal',
                                                        lineHeight: 1.3,
                                                    },
                                                    bgcolor: isOn ? midnight.primary : midnight.surfaceHighest,
                                                    color: isOn ? '#392700' : midnight.onSurface,
                                                    border: '1px solid',
                                                    borderColor: isOn ? midnight.primary : 'rgba(255,255,255,0.08)',
                                                    '&:hover': {
                                                        bgcolor: isOn ? '#feb700' : midnight.surfaceContainer,
                                                    },
                                                }}
                                            />
                                        );
                                    })}
                                </Box>
                            ) : current.type === 'richselect' ? (
                                <ContestRichSelect
                                    value={answers[current.id] || ''}
                                    options={richSelectOptionsForQuestion(context, current.id)}
                                    onChange={v =>
                                        setAnswers(prev => ({
                                            ...prev,
                                            [current.id]: v,
                                            ...(current.id === 'contest_type'
                                                ? { selected_criteria: defaultCriteriaSelection(v) }
                                                : {}),
                                        }))
                                    }
                                />
                            ) : current.type === 'select' ? (
                                <ContestRichSelect
                                    value={answers[current.id] || ''}
                                    options={
                                        current.id === 'first_phase'
                                            ? getPhaseSelectOptionsFromValues(
                                                  parseSelectedPhases(answers.selected_phases),
                                              )
                                            : getPhaseSelectOptionsFromValues((current.options || []).map(o => o.value))
                                    }
                                    onChange={v =>
                                        setAnswers(prev => ({
                                            ...prev,
                                            [current.id]: v,
                                        }))
                                    }
                                />
                            ) : current.type === 'date' ? (
                                <CustomDatePicker
                                    brandLuxe
                                    calendarOnly
                                    value={answers[current.id] || undefined}
                                    onChange={val =>
                                        setAnswers(prev => ({
                                            ...prev,
                                            [current.id]: datePickerValueToYyyyMmDd(val),
                                        }))
                                    }
                                    minDate={todayStart()}
                                    withTime={false}
                                    withTimezone={false}
                                    sx={contestWizardDatePickerSx}
                                />
                            ) : (
                                <TextField
                                    fullWidth
                                    multiline={current.multiline}
                                    minRows={current.multiline ? 3 : 1}
                                    placeholder={current.placeholder}
                                    value={answers[current.id] || ''}
                                    onChange={e =>
                                        setAnswers(prev => ({
                                            ...prev,
                                            [current.id]: e.target.value,
                                        }))
                                    }
                                    sx={editorialInputSx}
                                />
                            )}
                        </motion.div>
                    )}
                    {generating && (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                            <Typography sx={{ color: midnight.secondary, textAlign: 'center', py: 4 }}>
                                Generating your draft…
                            </Typography>
                        </motion.div>
                    )}
                </AnimatePresence>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3 }}>
                <Button onClick={handleClose} sx={{ color: midnight.onSurfaceVariant }}>
                    Cancel
                </Button>
                <Button
                    onClick={handleNext}
                    disabled={generating || !current || !isQuestionAnswered(current, answers)}
                    sx={{
                        color: '#392700',
                        bgcolor: midnight.primary,
                        fontWeight: 700,
                        borderRadius: 999,
                        px: 3,
                        '&:hover': { bgcolor: '#feb700' },
                    }}>
                    {index < questions.length - 1 ? 'Next' : 'Generate'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
